import { OPTIMISATION_BASE_STYLES } from '@/dashboards/CampaignPlanner/helpers/optimisationBaseStyles';
import {
    FEATURE_FLAG_ACTIONS,
    FEATURE_FLAG_SUBJECTS,
} from '@/features/Casl';
import { useAbilityContext } from '@/features/Casl/hooks';
import { OptimisationBase as OptimisationBaseType } from '@/optimizer';
import { Typography } from '@analytical-alley/ui';

interface TypeStepProps {
    optimizationBase: OptimisationBaseType;
    onChange: (value: OptimisationBaseType) => void;
}

interface OptionDef {
    id: OptimisationBaseType;
    title: string;
    summary: string;
    detail: string;
    /**
     * Feature-flag subject gating this option. When set, the option is only
     * shown if the user has EDIT access to it. `budget` (Media Investment) is
     * always available and has no subject.
     */
    subject?:
        | FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_KPI_OPTIMISATION_MEDIA_KPI
        | FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_KPI_OPTIMISATION_TOTAL_KPI;
}

const OPTIONS: OptionDef[] = [
    {
        id: 'budget',
        title: 'Media Investment',
        summary: 'Fit a fixed media budget.',
        detail:
            'You define how much you want to spend; the optimiser allocates that amount across channels to deliver the highest media KPI within the budget.',
    },
    {
        id: 'media_kpi',
        title: 'Media Contribution',
        summary: 'Maximise media-driven KPI.',
        detail:
            'You define a target media-driven KPI (e.g. media-driven sales). The optimiser proposes the spend required to reach that target across channels.',
        subject:
            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_KPI_OPTIMISATION_MEDIA_KPI,
    },
    {
        id: 'total_kpi',
        title: 'Total Contribution',
        summary: 'Target overall KPI including the base.',
        detail:
            'You define a target for total KPI (base + media). The optimiser proposes the spend needed to reach that overall outcome, accounting for non-media baseline.',
        subject:
            FEATURE_FLAG_SUBJECTS.CAMPAIGN_OPTIMIZATION_DASHBOARD_KPI_OPTIMISATION_TOTAL_KPI,
    },
];

export const TypeStep = ({ optimizationBase, onChange }: TypeStepProps) => {
    const { ability } = useAbilityContext();

    const options = OPTIONS.filter(
        (option) =>
            !option.subject ||
            ability.can(FEATURE_FLAG_ACTIONS.EDIT, option.subject),
    );

    return (
        <fieldset>
            <legend className="sr-only">Optimisation Target</legend>
            <Typography variant="bodyS" className="mb-2 text-gray-500">
                Choose what the optimiser should aim for:
            </Typography>

            <div
                role="radiogroup"
                aria-label="Optimisation Target"
                className="flex flex-col gap-2"
            >
                {options.map((option) => {
                    const checked = optimizationBase === option.id;
                    const style = OPTIMISATION_BASE_STYLES[option.id];
                    const inputId = `optimization-base-${option.id}`;
                    const descId = `${inputId}-description`;

                    return (
                        <label
                            key={option.id}
                            htmlFor={inputId}
                            className={`relative flex items-start gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                                checked
                                    ? `${style.border.split(' ')[0]} bg-white/5`
                                    : 'border-white/10 hover:border-white/30'
                            }`}
                        >
                            <input
                                type="radio"
                                id={inputId}
                                name="optimizationBase"
                                value={option.id}
                                checked={checked}
                                aria-describedby={descId}
                                onChange={() => onChange(option.id)}
                                className="mt-1 h-4 w-4 cursor-pointer text-[#1C64F2] focus:ring-1 focus:ring-[#1C64F2]"
                            />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Typography
                                        variant="bodyS"
                                        className="font-semibold"
                                    >
                                        {option.title}
                                    </Typography>
                                    <span
                                        className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${style.tag}`}
                                    >
                                        {option.summary}
                                    </span>
                                </div>
                                <Typography
                                    variant="bodyXS"
                                    id={descId}
                                    className="mt-1 text-gray-500"
                                >
                                    {option.detail}
                                </Typography>
                            </div>
                        </label>
                    );
                })}
            </div>
        </fieldset>
    );
};
